import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';

interface StreakChipProps {
  /** Реальный стрик из dashboard-stats. undefined — ещё грузится. */
  streak?: number;
  /** Текущая серия верных подряд. Приписка появляется с 2. */
  combo?: number;
}

/** Плашка слева в шапке: огонёк стрика и, когда серия разошлась, её счётчик. */
export function StreakChip({ streak, combo = 0 }: StreakChipProps) {
  const { t } = useTranslation();
  // Серии нет или она ещё грузится — на телефоне плашку не показываем (`.st-chip.is-empty` в index.css):
  // «✦ 0» и «—» только отнимали место у счётчика (приёмка A1-14, B9).
  const empty = !streak || streak <= 0;
  return (
    <span className={cn('st-chip', empty && 'is-empty')}>
      <span className="st-spark" aria-hidden>
        ✦
      </span>
      <b>{streak ?? '—'}</b>
      <span className="st-chip-label">{t('session.streak', { count: streak ?? 0 })}</span>
      {combo >= 2 && (
        <span className="st-chip-combo">· {t('session.comboRun', { count: combo })}</span>
      )}
    </span>
  );
}

/** Статичная плашка для финального раунда (стрика там не показываем). */
export function StageChip({ children }: { children: ReactNode }) {
  return (
    <span className="st-chip">
      <span className="st-spark" aria-hidden>
        ✦
      </span>
      <span className="st-chip-label">{children}</span>
    </span>
  );
}

interface StageTopProps {
  left: ReactNode;
  center?: ReactNode;
  right: ReactNode;
}

/** Общая шапка сцены: плашка слева, прогресс по центру, действия справа. */
export function StageTop({ left, center, right }: StageTopProps) {
  return (
    <header className="st-top">
      <div className="st-top-side">{left}</div>
      <div className="st-top-mid">{center}</div>
      <div className="st-top-side st-top-actions">{right}</div>
    </header>
  );
}

interface StageActionProps {
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  /** true — необратимое действие («завершить»): акцентная обводка, чтобы его
   *  нельзя было спутать с обычным выходом, который сохраняет тренировку. */
  strong?: boolean;
  children: ReactNode;
}

/** Текстовая кнопка шапки («выйти» / «завершить»). */
export function StageAction({ onClick, disabled, busy, strong, children }: StageActionProps) {
  return (
    <button
      type="button"
      className={cn('st-link', strong && 'is-strong')}
      onClick={onClick}
      disabled={disabled}
      aria-busy={busy}
    >
      {children}
    </button>
  );
}

interface StageFinishProps {
  onFinish: () => void;
  disabled?: boolean;
  finishing?: boolean;
}

/**
 * «Завершить» обрывает тренировку без возврата — через окно подтверждения (решение владельца 2026-10-21:
 * второе нажатие по той же кнопке никто не замечал). В окне по умолчанию в фокусе «Продолжить», чтобы
 * случайный Enter не завершал тренировку.
 */
export function StageFinish({ onFinish, disabled, finishing }: StageFinishProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const label = finishing ? t('session.finishing', 'Завершаем…') : t('session.finishShort', 'завершить');

  return (
    <>
      <button
        type="button"
        className="st-link is-strong"
        onClick={() => {
          if (!disabled && !finishing) setOpen(true);
        }}
        disabled={disabled}
        aria-busy={finishing}
        aria-haspopup="dialog"
      >
        {label}
      </button>
      {open && (
        <FinishConfirm
          onCancel={() => setOpen(false)}
          onConfirm={() => {
            setOpen(false);
            onFinish();
          }}
        />
      )}
    </>
  );
}

/** Окно «Завершить тренировку?»: на телефоне — лист снизу, на компьютере — по центру. */
function FinishConfirm({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
  const { t } = useTranslation();
  const titleId = useId();
  const bodyId = useId();
  const keepRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    keepRef.current?.focus();
    // Клавиши сцены (Enter — ответ, цифры — варианты) слушают window: пока окно открыто, до них не доходит.
    // Нажатие на кнопку в фокусе срабатывает как обычно.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
      if (e.key !== 'Tab') e.stopPropagation();
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      previous?.focus?.();
    };
  }, [onCancel]);

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-end justify-center sm:items-center sm:p-6">
      <div aria-hidden className="absolute inset-0 animate-fadeIn bg-[rgba(8,8,10,0.62)]" onClick={onCancel} />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        className="relative w-full max-w-[420px] animate-pop rounded-t-[20px] border border-solid border-line bg-card px-5 pb-[max(20px,env(safe-area-inset-bottom))] pt-5 sm:rounded-[20px] sm:p-6"
      >
        <h2 id={titleId} className="font-serif text-[22px] font-medium leading-tight tracking-[-0.01em] text-fg">
          {t('session.finishDialogTitle')}
        </h2>
        <p id={bodyId} className="mt-2 text-[15px] leading-snug text-muted">
          {t('session.finishDialogBody')}
        </p>
        <div className="mt-5 flex gap-3">
          <button
            ref={keepRef}
            type="button"
            onClick={onCancel}
            className="h-12 flex-1 rounded-pill border border-solid border-line text-[15px] font-semibold text-fg transition-colors hover:bg-white/[0.04]"
          >
            {t('session.finishDialogNo')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="h-12 flex-1 rounded-pill bg-accent text-[15px] font-semibold text-fg-inv transition-colors hover:bg-accent-hi"
          >
            {t('session.finishDialogYes')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
